import React, { useState, useContext, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";

import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";

import { i18n } from "../../translate/i18n";
import { getRegistrationStatus } from "../../services/settings.api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useWebInfo } from "../../context/WebInfoContext";
import AuthLayout, { useAuthFormStyles } from "../../components/AuthLayout";

// Doc 0446, fase 3: misma lógica de siempre; la presentación vive en AuthLayout.
const Login = () => {
  const classes = useAuthFormStyles();
  const isMounted = useRef(true);

  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const { handleLogin } = useContext(AuthContext);
  const [viewregister, setviewregister] = useState("disabled");

  const { webInfo, updatePageTitle } = useWebInfo();

  useEffect(() => {
    isMounted.current = true;

    if (webInfo?.title && isMounted.current) {
      updatePageTitle(webInfo.title);
    }

    return () => {
      isMounted.current = false;
    };
  }, [webInfo, updatePageTitle]);

  useEffect(() => {
    isMounted.current = true;

    // ✅ NUEVO: Verificar estado de registro usando endpoint público que verifica AMBAS configuraciones
    const fetchRegistrationStatus = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await getRegistrationStatus({
          signal: controller.signal,
        });

        clearTimeout(timeoutId);
        if (!isMounted.current) return;

        // ✅ El registro está habilitado solo si AMBAS configuraciones están en "enabled"
        const registrationEnabled = response?.data?.enabled === true;
        setviewregister(registrationEnabled ? "enabled" : "disabled");
      } catch (error) {
        if (!isMounted.current) return;
        // ✅ Por defecto, deshabilitado si hay error o no existe
        setviewregister("disabled");
      }
    };

    fetchRegistrationStatus();

    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handlSubmit = async (e) => {
    e.preventDefault();

    // Intento con rememberMe opcional; si AuthContext no lo soporta, fallback
    try {
      await handleLogin(user, { rememberMe });
    } catch (err) {
      handleLogin(user);
    }
  };

  return (
    <AuthLayout title={i18n.t("authScreens.loginTitle")} subtitle={i18n.t("authScreens.loginSubtitle")}>
      <form noValidate onSubmit={handlSubmit} className={classes.form}>
        <TextField
          variant="outlined"
          required
          fullWidth
          id="email"
          label={i18n.t("login.form.email")}
          name="email"
          value={user.email}
          onChange={handleChangeInput}
          autoComplete="email"
          autoFocus
          className={classes.field}
        />

        <TextField
          variant="outlined"
          required
          fullWidth
          name="password"
          label={i18n.t("login.form.password")}
          type={showPassword ? "text" : "password"}
          id="password"
          value={user.password}
          onChange={handleChangeInput}
          autoComplete="current-password"
          className={classes.field}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={i18n.t("authScreens.showPassword")}
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <div className={classes.rowBetween}>
          <FormControlLabel
            control={
              <Switch
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={<Typography style={{ fontSize: 14 }}>{i18n.t("authScreens.remember")}</Typography>}
          />
          <Link component={RouterLink} to="/forgot-password" className={classes.link} style={{ fontSize: 14 }}>
            {i18n.t("passwordRecovery.forgotLink")}
          </Link>
        </div>

        <Button type="submit" fullWidth variant="contained" className={classes.submit}>
          {i18n.t("authScreens.loginButton")}
        </Button>

        {viewregister === "enabled" && (
          <Typography className={classes.muted}>
            {i18n.t("authScreens.noAccount")}{" "}
            <Link component={RouterLink} to="/signup" className={classes.link}>
              {i18n.t("authScreens.register")}
            </Link>
          </Typography>
        )}
      </form>
    </AuthLayout>
  );
};

export default Login;
